import { useCallback, useEffect, useRef, useState } from 'react'
import type { BattleSnapshot, Hit, RealtimeMessage, StateResponse } from '@boss/shared'
import { apiUrl } from '../api'

export interface BattleHandlers {
  onHit?: (hit: Hit, prev: BattleSnapshot, next: BattleSnapshot) => void
  onReset?: (snapshot: BattleSnapshot) => void
  /** A full snapshot was loaded (first load, battle change, or a jump after a long gap). */
  onLoad?: (snapshot: BattleSnapshot, recent: Hit[]) => void
}
/** How updates arrive: `relay` (Cloudflare WebSocket) and `ably` are live pushes, `polling` is the fallback. */
export type Transport = 'connecting' | 'polling' | 'ably' | 'relay'

const HIT_SPACING_MS = 220
/** A burst bigger than this only animates its last few hits; the rest jump. */
const MAX_ANIMATED = 8
/** The light poll carries this many recent hits; more than that between polls means a gap. */
const LIGHT_RECENT = 20
const POLL_FALLBACK_MS = Number(import.meta.env.VITE_POLL_MS ?? 1500)
const POLL_RECONCILE_MS = 15_000
const ABLY_AUTH_URL = (() => {
  const v = import.meta.env.VITE_ABLY_AUTH_URL as string | undefined
  return v ? (/^https?:\/\//.test(v) ? v : apiUrl(v)) : undefined
})()
const ABLY_CHANNEL = (import.meta.env.VITE_ABLY_CHANNEL as string | undefined) ?? 'boss:state'
/** Cloudflare relay (apps/relay). http(s):// or ws(s)://, no path. */
const RELAY_URL = ((import.meta.env.VITE_RELAY_URL as string | undefined) ?? '').replace(/\/+$/, '').replace(/^http/, 'ws')
const RELAY_SHARDS = Math.max(1, Number(import.meta.env.VITE_RELAY_SHARDS ?? 4) || 1)

function withHit(prev: BattleSnapshot, hit: Hit): BattleSnapshot {
  const defeated = hit.progressAfter >= 1
  let value = 0
  const goals = prev.goals.map((g) => {
    if (g.id !== hit.goalId) return g
    value = g.price ?? 0
    return { ...g, openings: g.openings + 1, points: hit.pointsAfter, progress: Math.min(1, hit.pointsAfter / g.target), complete: hit.pointsAfter >= g.target }
  })
  return {
    ...prev,
    goals,
    progress: hit.progressAfter,
    version: hit.seq,
    totalOpened: prev.totalOpened + 1,
    totalValue: prev.totalValue + value,
    defeated,
    defeatedAt: defeated ? (prev.defeatedAt ?? hit.receivedAt) : prev.defeatedAt,
  }
}

/**
 * Owns the client copy of the battle. Loads a snapshot, then keeps it current
 * through the relay or Ably when configured and through polling otherwise. Hits
 * are queued and played one at a time so a burst still animates hit by hit.
 */
export function useBattle(handlers: BattleHandlers) {
  const [snapshot, setSnapshot] = useState<BattleSnapshot | null>(null)
  const [transport, setTransport] = useState<Transport>('connecting')
  const [error, setError] = useState<string | null>(null)

  const st = useRef({
    snapshot: null as BattleSnapshot | null,
    /** Highest seq either applied or queued. Gap detection compares against this. */
    expected: 0,
    queue: [] as Hit[],
    /** Server snapshot to reconcile to once the queue drains. */
    pending: null as BattleSnapshot | null,
    timer: undefined as number | undefined,
    handlers,
  })
  st.current.handlers = handlers

  const commit = useCallback((s: BattleSnapshot) => {
    st.current.snapshot = s
    setSnapshot(s)
  }, [])

  const replace = useCallback(
    (s: BattleSnapshot, announce: boolean) => {
      const c = st.current
      c.queue = []
      c.pending = null
      if (c.timer) window.clearTimeout(c.timer)
      c.timer = undefined
      c.expected = s.version
      commit(s)
      if (announce) c.handlers.onReset?.(s)
    },
    [commit],
  )

  const drain = useCallback(() => {
    const c = st.current
    if (c.timer) return
    const step = () => {
      const hit = c.queue.shift()
      if (!hit) {
        c.timer = undefined
        if (c.pending) {
          const p = c.pending
          c.pending = null
          if (p.version >= (c.snapshot?.version ?? 0)) commit(p)
        }
        return
      }
      const prev = c.snapshot
      if (prev && hit.seq > prev.version) {
        const next = withHit(prev, hit)
        commit(next)
        c.handlers.onHit?.(hit, prev, next)
      }
      c.timer = window.setTimeout(step, HIT_SPACING_MS)
    }
    step()
  }, [commit])

  const enqueue = useCallback(
    (hits: Hit[], final: BattleSnapshot) => {
      const c = st.current
      for (const h of hits) {
        if (h.seq > c.expected) {
          c.queue.push(h)
          c.expected = h.seq
        }
      }
      // Counts and progress on each hit are absolute, so skipping the older ones is safe;
      // totals reconcile when the final snapshot is committed after the queue drains.
      if (c.queue.length > MAX_ANIMATED) c.queue.splice(0, c.queue.length - MAX_ANIMATED)
      c.pending = final
      drain()
    },
    [drain],
  )

  /** Apply a server response. `mode` says which URL produced it. */
  const absorb = useCallback(
    (data: StateResponse, mode: 'full' | 'light' | 'since') => {
      const c = st.current
      const newBattle = !c.snapshot || c.snapshot.battleId !== data.snapshot.battleId
      if (newBattle) {
        replace(data.snapshot, c.snapshot != null)
        if (data.recent) c.handlers.onLoad?.(data.snapshot, data.recent)
        return data.recent ? 'ok' : 'need-full'
      }
      if (mode === 'since') {
        // Not used by the app any more (every visitor request stays cacheable); kept for tooling.
        if (data.hits.length) enqueue(data.hits, data.snapshot)
        return 'ok'
      }
      // Light or full: derive what is new from the recent list (newest first).
      const recent = data.recent ?? []
      const newer = recent.filter((h) => h.seq > c.expected).sort((a, b) => a.seq - b.seq)
      if (newer.length) {
        if (mode === 'light' && newer[0].seq !== c.expected + 1 && recent.length >= LIGHT_RECENT) {
          // More happened than the light poll carries: take the (cached) full load instead.
          return 'need-full'
        }
        if (mode === 'full' && newer[0].seq !== c.expected + 1) {
          // Far behind (a bulk admin change, a tab that slept): jump, animate only the tail.
          replace(data.snapshot, true)
          c.handlers.onLoad?.(data.snapshot, recent)
          return 'ok'
        }
        enqueue(newer, data.snapshot)
        if (mode === 'full') c.handlers.onLoad?.(data.snapshot, recent)
      } else if (data.snapshot.version < c.expected) {
        // Server is behind what we already show (a stale cache racing a live push). Ignore.
      } else if (!c.queue.length) {
        // Out-of-band change (admin set-count) or just a reconcile of totals.
        const changed =
          data.snapshot.defeated !== c.snapshot!.defeated ||
          data.snapshot.progress !== c.snapshot!.progress ||
          data.snapshot.goals.some((g, i) => g.points !== c.snapshot!.goals[i]?.points)
        if (changed) replace(data.snapshot, true)
        else commit(data.snapshot)
        if (changed && mode === 'full' && data.recent) c.handlers.onLoad?.(data.snapshot, data.recent)
      }
      return 'ok'
    },
    [commit, enqueue, replace],
  )

  const fetchState = useCallback(
    async (mode: 'full' | 'light' | 'since' = 'light') => {
      try {
        const c = st.current
        const url = mode === 'since' ? `/api/state?since=${c.expected}` : mode === 'full' ? '/api/state?full=1' : '/api/state'
        // Never read these from the browser's HTTP cache: a CDN in front may rewrite the
        // one-second max-age to hours (Cloudflare's Browser Cache TTL does), and the page
        // would then poll its own disk cache forever. The CDN still serves its edge copy.
        const res = await fetch(apiUrl(url), { cache: 'no-store' })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = (await res.json()) as StateResponse
        const outcome = absorb(data, mode)
        if (outcome === 'need-full' && mode !== 'full') await fetchState('full')
        setError(null)
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      }
    },
    [absorb],
  )

  // Initial load: the backlog for the feed.
  useEffect(() => {
    void fetchState('full')
  }, [fetchState])

  // Polling: the only transport without a push channel, a slow reconcile with one.
  useEffect(() => {
    const live = transport === 'ably' || transport === 'relay'
    const interval = live ? POLL_RECONCILE_MS : POLL_FALLBACK_MS
    const tick = () => {
      // No snapshot yet means the first load failed (API restarting, network blip): keep retrying it.
      void fetchState(st.current.snapshot ? 'light' : 'full')
    }
    const id = window.setInterval(tick, interval)
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [transport, fetchState])

  /** Shared handling for a pushed message, from the relay or Ably. */
  const onPush = useCallback(
    (m: RealtimeMessage) => {
      const c = st.current
      if (!c.snapshot) return // initial fetch still in flight; polling reconciles
      if (m.type === 'reset') {
        if (m.snapshot.battleId !== c.snapshot.battleId) {
          replace(m.snapshot, true)
          void fetchState('full')
        } else if (m.resync) {
          // The relay's greeting: catch up quietly if it is ahead of us (cached light poll).
          if (m.snapshot.version > c.expected) void fetchState('light')
        } else replace(m.snapshot, true)
        return
      }
      if (m.snapshot.battleId !== c.snapshot.battleId) {
        replace(m.snapshot, true)
        void fetchState('full')
        return
      }
      if (m.hit.seq <= c.expected) return
      if (m.hit.seq === c.expected + 1) enqueue([m.hit], m.snapshot)
      else void fetchState('light') // gap: the cached poll carries the last 20, or escalates to a full load
    },
    [enqueue, fetchState, replace],
  )

  // Cloudflare relay: preferred push channel when configured.
  useEffect(() => {
    if (!RELAY_URL) return
    let closed = false
    let ws: WebSocket | undefined
    let attempt = 0
    let retry: number | undefined
    const shard = Math.floor(Math.random() * RELAY_SHARDS)
    const connect = () => {
      if (closed) return
      ws = new WebSocket(`${RELAY_URL}/ws?shard=${shard}`)
      ws.onopen = () => {
        attempt = 0
        setTransport('relay')
      }
      ws.onmessage = (ev) => {
        if (typeof ev.data !== 'string' || ev.data === 'pong') return
        try {
          onPush(JSON.parse(ev.data) as RealtimeMessage)
        } catch {
          /* ignore malformed frames */
        }
      }
      ws.onclose = () => {
        if (closed) return
        setTransport((t) => (t === 'relay' || t === 'connecting' ? 'polling' : t))
        attempt += 1
        retry = window.setTimeout(connect, Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5)) + Math.random() * 500)
      }
      ws.onerror = () => ws?.close()
    }
    connect()
    const keepalive = window.setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send('ping'), 45_000)
    return () => {
      closed = true
      window.clearInterval(keepalive)
      if (retry) window.clearTimeout(retry)
      ws?.close()
    }
  }, [onPush])

  // Ably: optional alternative push channel.
  useEffect(() => {
    if (!ABLY_AUTH_URL) {
      if (!RELAY_URL) setTransport('polling')
      return
    }
    let closed = false
    let realtime: { close(): void } | undefined
    import('ably')
      .then((mod) => {
        if (closed) return
        const Ably = mod.default ?? mod
        const rt = new Ably.Realtime({ authUrl: ABLY_AUTH_URL, authMethod: 'GET' })
        realtime = rt
        rt.connection.on('connected', () => setTransport((t) => (t === 'relay' ? t : 'ably')))
        rt.connection.on((change) => {
          if (['disconnected', 'suspended', 'failed', 'closed'].includes(change.current)) setTransport((t) => (t === 'ably' ? 'polling' : t))
        })
        const channel = rt.channels.get(ABLY_CHANNEL, { params: { rewind: '1m' } })
        void channel.subscribe((msg) => onPush(msg.data as RealtimeMessage))
      })
      .catch((e) => {
        console.warn('[ably] unavailable, polling instead', e)
        setTransport((t) => (t === 'relay' ? t : 'polling'))
      })
    return () => {
      closed = true
      realtime?.close()
    }
  }, [onPush])

  return { snapshot, transport, error, refetch: () => fetchState('full') }
}
